import React, { useEffect } from 'react';

// import props
import { ListItemsColumnProps, ListItemsFilterByOptionProps, ListItemsProps } from './props';

// assets
import { CommandBar, ContextualMenu, ContextualMenuItemType, DirectionalHint, IColumn, ICommandBarItemProps, Icon, IContextualMenuItem, IContextualMenuProps, SearchBox, SelectionMode, ShimmeredDetailsList, Stack } from '@fluentui/react';
import Label from '../../typography/label';
import Pagination from '../pagination';
import Text from '../../typography/text';
import PivotButton, { IButtonProps } from '../buttons/pivot';
import GenericFilterPanel from './filters/generic';
import SelectedFilter from './filters/selected';

const ListItems: React.FC<ListItemsProps> = (props: ListItemsProps) => {
    const [activeContextMenuProps, setActiveContextMenuProps] = React.useState<IContextualMenuProps | undefined>();
    const [activeFilterColumn, setActiveFilterColumn] = React.useState<ListItemsColumnProps | undefined>();

    const _onColumnHeaderClicked = (ev: React.MouseEvent<HTMLElement>, column: ListItemsColumnProps) => {
        const menuItems: IContextualMenuItem[] = [];

        if (column.enableOrderBy) {
            menuItems.push({
                key: 'sortSection',
                itemType: ContextualMenuItemType.Section,
                sectionProps: {
                    items: [
                        {
                            key: 'sortAsc',
                            text: 'A to Z (Ascending)',
                            iconProps: { iconName: "SortUp" },
                            onClick: () => {
                                if (props.onOrderByChange) {
                                    setActiveContextMenuProps(undefined);
                                    props.onOrderByChange({ fieldName: column.fieldName || column.key, direction: 'asc' });
                                }
                            }
                        },
                        {
                            key: 'sortDesc',
                            text: 'Z to A (Descending)',
                            iconProps: { iconName: "SortDown" },
                            onClick: () => {
                                if (props.onOrderByChange) {
                                    setActiveContextMenuProps(undefined);
                                    props.onOrderByChange({ fieldName: column.fieldName || column.key, direction: 'desc' });
                                }
                            }
                        }
                    ]
                }
            })
        }

        if (column.enableFilter) {
            if (menuItems.length > 0) {
                menuItems.push({
                    key: 'divider_1',
                    itemType: ContextualMenuItemType.Divider
                });
            }

            menuItems.push({
                key: 'filterBy',
                iconProps: { iconName: 'Filter' },
                text: 'Filter by',
                title: 'Filter by',
                onClick: function () {
                    setActiveContextMenuProps(undefined);
                    setActiveFilterColumn(column);
                }
            });
        }

        setActiveContextMenuProps({ items: menuItems, target: ev.currentTarget as HTMLElement });
    }

    const _onRenderColumnHeader = (column: ListItemsColumnProps) => {
        return <Stack horizontal tokens={{ childrenGap: 10 }} onClick={(ev) => {
            if (column.enableFilter || column.enableOrderBy) {
                _onColumnHeaderClicked(ev, column);
            }
        }}>
            <Label size={'xsmall'} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{column.name}</Label>
            {column.enableFilter || column.enableOrderBy ? <Icon style={{ marginTop: 1, fontSize: 10 }} iconName={'ChevronDown'} /> : null}
        </Stack>
    }

    const getColumns = (): IColumn[] => {
        const columns: IColumn[] = props.columns.map((column) => {
            const isSorted = props.orderBy && ((column.fieldName && props.orderBy.fieldName === column.fieldName) || (!column.fieldName && props.orderBy.fieldName === column.key));

            let metadata: IColumn = {
                key: column.key,
                name: column.name,
                minWidth: column.minWidth,
                maxWidth: column.maxWidth,
                isMultiline: true,
                isSorted,
                isSortedDescending: isSorted && props.orderBy?.direction === 'desc',
                onRenderHeader: () => _onRenderColumnHeader(column),
                onRender: column.onRender
            };

            return metadata;
        });

        if (props.shownColumns) {
            return columns.filter((col) => (props.shownColumns || []).indexOf(col.key) > -1);
        } else {
            return columns;
        }
    }

    const renderCommandBar = () => {
        const { commandBar, properties, loaded } = props;

        let items: ICommandBarItemProps[] = [];
        let farItems: ICommandBarItemProps[] = [];

        if (properties?.hideSearch) {
            items.push({
                key: 'search',
                onRender: () => {
                    return <SearchBox disabled={!loaded} styles={{ root: { minWidth: 250, marginRight: 10 } }} />;
                }
            });
        }

        if (commandBar) {
            items = [...items, ...(commandBar.items || [])];
            farItems = [...farItems, ...(commandBar.farItems || [])];
        }

        return <>
            {(items.length > 0 || farItems.length > 0) ? <CommandBar items={items}
                farItems={farItems}
                styles={{ root: { padding: 0, margin: 0, height: 'unset' } }} /> : null}
        </>;
    }

    const renderPagination = (showTotalItems?: boolean) => {
        const { pagination, properties, items, loaded, onPageChange } = props;

        if (!loaded) {
            return null;
        } else if (pagination && items.length > 0) {
            return <Pagination goToPage={onPageChange}
                currentPage={pagination.currentPage}
                totalPage={pagination.lastPage}
                text={showTotalItems ? `${pagination.total} ${props.itemText || 'items'} found` : undefined} />;
        } else if (!properties?.hideTotalItems && showTotalItems && items.length > 0) {
            return <Text>{items.length} items found</Text>;
        } else {
            return null;
        }
    }

    const renderQuickFilters = () => {
        const { quickFilters } = props;

        if (!quickFilters || quickFilters.length < 1) {
            return null;
        }

        return <Stack horizontal tokens={{ childrenGap: 20 }} styles={{ root: { padding: '1px 0px' } }}>
            {quickFilters.map((quickFilter) => {
                const buttons: IButtonProps[] = quickFilter.options.map((opt) => {
                    return {
                        key: opt.key,
                        text: opt.text,
                        active: quickFilter.selectedKey === opt.key,
                        onClick: () => quickFilter.onChange ? quickFilter.onChange(opt) : null
                    };
                });

                return <PivotButton buttons={buttons} />
            })}
        </Stack>
    }

    const renderFilterBy = () => {
        const { filterBy } = props;

        if (!filterBy || filterBy.length < 1) {
            return null;
        }

        return <Stack>
            <SelectedFilter filterBy={filterBy} onRemove={props.onFilterByChange} />
        </Stack>
    }

    const _onFilterApply = (selected: ListItemsFilterByOptionProps[], column: ListItemsColumnProps) => {
        const filterBy = props.filterBy || [];

        if (props.onFilterByChange) {
            const idx = (filterBy || []).findIndex((f) => column.fieldName ? f.key === column.fieldName : f.key === column.key);
            if (idx > -1 && selected.length < 1) {
                // check if no value is selected
                // clear the filter by
                filterBy.splice(idx, 1);
                props.onFilterByChange(filterBy);
            } else if (idx > -1 && selected.length > 0) {
                // check if current selected the same with new selection
                const currentSelectedKeys = filterBy[idx].selected.map((sel) => sel.key).sort().join(";#").toLowerCase();
                const newSelectedKeys = selected.map((sel) => sel.key).sort().join(";#").toLowerCase();

                if (currentSelectedKeys !== newSelectedKeys) {
                    filterBy[idx].selected = selected;
                    props.onFilterByChange(filterBy);
                }
            } else if (idx < 0 && selected.length > 0) {
                // check if there are no filter by using this key yet
                // add new filter by object
                filterBy.push({
                    key: column.fieldName || column.key,
                    selected,
                    text: column.name
                });
                props.onFilterByChange(filterBy);
            }
        }

        setActiveFilterColumn(undefined);
    }

    return <Stack key={props.key} tokens={{ childrenGap: 20 }}>
        {renderCommandBar()}
        {renderFilterBy()}
        {renderPagination(true)}
        {renderQuickFilters()}
        {!props.loaded || (props.loaded && props.items.length > 0) ? <ShimmeredDetailsList items={props.items}
            selectionMode={props.selectionMode || SelectionMode.none}
            columns={getColumns()}
            shimmerLines={10}
            enableShimmer={!props.loaded} /> : null}
        {props.loaded && props.items.length < 1 ? <Text size={'small'} style={{fontStyle: 'italic', marginTop: 15}}>No {props.itemText || 'items'} found</Text> : null}
        {renderPagination(false)}
        {activeContextMenuProps ? <ContextualMenu {...activeContextMenuProps} directionalHint={DirectionalHint.bottomLeftEdge} onDismiss={() => {
            setActiveContextMenuProps(undefined);
        }} /> : null}
        {activeFilterColumn ? <GenericFilterPanel title={`Filter by "${activeFilterColumn.name}"`}
            selected={(props.filterBy || []).find((f) => activeFilterColumn.fieldName ? f.key === activeFilterColumn.fieldName : f.key === activeFilterColumn.key)?.selected}
            onRetrieveOptions={activeFilterColumn.onRetrieveFilterOptions}
            onApply={(selected) => _onFilterApply(selected, activeFilterColumn)}
            onCancel={() => setActiveFilterColumn(undefined)} /> : null}
    </Stack>
};

export default ListItems;
